import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { atLeast } from '@voll-intelligence/ui-kit/breakpoints';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import { fmtWeekdayDate } from '../results/flight_format.js';
import { FlightDetailDrawerShell } from './flight_detail_drawer.js';
import {
  hasEnrichmentData,
  onTimeStatLabel,
  soldAsLabel,
  withTerminal,
  type FlightDetailCopy,
  type FlightEnrichmentSource,
} from './flight_detail_copy.js';
import {
  FlightDetailView,
  type FlightDetailHeader,
  type FlightTimelineLeg,
} from './flight_timeline.js';

/** O cabeçalho do voo como a oferta o descreve, antes das pernas pousarem. */
export interface FlightDetailSummary {
  carrier?: string;
  carrierName?: string;
  flightNumber?: string;
  from: string;
  to: string;
  /** Perna do round-trip, valor semântico; ausente no one-way. */
  dir?: 'outbound' | 'return';
}

/** Uma ponta de perna: aeroporto, o nome que o fornecedor deu e o instante local. */
interface FlightDetailEndpoint {
  iata: string;
  name?: string;
  at?: string;
}

/** Uma perna da linha do tempo, como o host a lê do fornecedor (GET /flight/legs). */
export interface FlightDetailLegSource extends FlightEnrichmentSource {
  origin: FlightDetailEndpoint;
  destination: FlightDetailEndpoint;
  durationSeconds?: number;
  /** Parada após a perna; ausente na última. */
  layoverSeconds?: number;
  operatedBy?: { code?: string; name?: string };
  flightNumber?: string;
  isConnection?: boolean;
  /** Código IATA da cia desta perna — compõe "continua no voo AD 2778". */
  carrierCode?: string;
  /** Direção num round-trip — divide o drawer em seções Ida/Volta. */
  direction?: 'outbound' | 'return';
}

/** O fetch das pernas, que o host faz. `stale`: a sessão de busca da oferta
 *  venceu, e nenhum novo fetch a traz de volta. */
export type FlightDetailLegsState<L extends FlightDetailLegSource = FlightDetailLegSource> =
  | { phase: 'loading' }
  | { phase: 'ready'; legs: readonly L[] }
  | { phase: 'error'; stale: boolean };

/** O nome de exibição de um aeroporto: o do cadastro do host, senão o do
 *  fornecedor, senão nada. */
export type FlightAirportName = (iata: string, supplierName?: string) => string | undefined;

/** `L` é a perna do host: o slot dos assentos a recebe de volta como veio. */
export interface FlightDetailPanelProps<L extends FlightDetailLegSource = FlightDetailLegSource> {
  /** Cabeçalho (cia/voo/rota), mostrado antes das pernas carregarem. */
  summary: FlightDetailSummary;
  legs: FlightDetailLegsState<L>;
  copy: FlightDetailCopy;
  /** O idioma das datas. */
  locale: string;
  airportName: FlightAirportName;
  /** O mapa de assentos é do host (#4997): ele desenha a seção sob o detalhe e
   *  abre o painel cheio por `openPanel`, que troca o corpo do drawer. */
  renderSeats?: (legs: readonly L[], openPanel: (panel: ReactNode) => void) => ReactNode;
  onClose: () => void;
  /** Rodapé "Selecionar voo" — ausente ⇒ sem rodapé (card congelado/leitura). */
  onSelect?: () => void;
  /** Por qual porta o drawer foi aberto. `'seats'` (o link "Assentos" do card
   *  de checkout, #5331) rola até a seção do mapa quando as pernas pousam, e a
   *  sessão de busca vencida vira o aviso de que o mapa acabou. NÃO abre o painel
   *  de uma perna: quem escolhe a perna é a pessoa, tocando a faixa — abrir a
   *  primeira por conta própria escolheria por ela. */
  focus?: 'seats';
}

/** HH:MM do instante local-naive do flight-business (`...T18:40:00...`), sem
 *  converter fuso (o backend já manda local). Vazio quando não casa. */
function fmtClock(at?: string): string {
  if (!at) return '';
  const m = /T(\d{2}:\d{2})/.exec(at);
  return m?.[1] ?? '';
}

/** Segundos → "1h 50min" / "50min". Vazio quando ausente/zero. */
function fmtDuration(seconds?: number): string {
  if (!seconds || seconds <= 0) return '';
  const mins = Math.round(seconds / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return m > 0 ? `${String(h)}h ${String(m)}min` : `${String(h)}h`;
  return `${String(m)}min`;
}

/** Cabeçalho da cia para a vista compartilhada, semeado do `summary` da oferta. A
 *  perna ("Ida"/"Volta") é localizada aqui; o logo é a marca vetorial da cia,
 *  resolvida pela própria vista compartilhada. */
function toDetailHeader(summary: FlightDetailSummary, copy: FlightDetailCopy): FlightDetailHeader {
  const flightNo = [summary.carrier, summary.flightNumber].filter(Boolean).join(' ');
  const route = [summary.from, summary.to].filter(Boolean).join(' → ');
  const dirText = summary.dir === 'outbound' ? copy.dirOutbound : copy.dirReturn;
  const dirLabel = summary.dir ? dirText : undefined;
  return {
    ...(summary.carrier ? { carrier: summary.carrier } : {}),
    ...(summary.carrierName ? { carrierName: summary.carrierName } : {}),
    ...(flightNo ? { flightNo } : {}),
    ...(route ? { route } : {}),
    ...(dirLabel ? { dirLabel } : {}),
  };
}

/** Cabeçalho de uma SEÇÃO do round-trip (Ida/Volta), derivado das pernas da
 *  direção: cia e nº da primeira perna, rota extremo-a-extremo e a data local
 *  ("Ida · Seg, 10 ago"). `summary.carrierName` só vale quando a cia da seção é a
 *  mesma do card (num combo misto o nome do card seria o da outra cia). */
function toSectionHeader(
  legs: readonly FlightDetailLegSource[],
  summary: FlightDetailSummary,
  dir: 'outbound' | 'return',
  copy: FlightDetailCopy,
  locale: string,
): FlightDetailHeader {
  const first = legs[0];
  const last = legs.at(-1);
  const carrier = first?.carrierCode ?? summary.carrier;
  const flightNo = [first?.carrierCode, first?.flightNumber].filter(Boolean).join(' ');
  const route = [first?.origin.iata, last?.destination.iata].filter(Boolean).join(' → ');
  const dateLabel = first?.origin.at ? fmtWeekdayDate(first.origin.at, locale) : '';
  const dirLabel = [dir === 'outbound' ? copy.dirOutbound : copy.dirReturn, dateLabel]
    .filter(Boolean)
    .join(' · ');
  return {
    ...(carrier ? { carrier } : {}),
    ...(carrier === summary.carrier && summary.carrierName
      ? { carrierName: summary.carrierName }
      : {}),
    ...(flightNo ? { flightNo } : {}),
    ...(route ? { route } : {}),
    dirLabel,
  };
}

/** O que os rótulos da linha do tempo leem: os textos e o nome dos aeroportos. */
type TimelineText = { copy: FlightDetailCopy; airportName: FlightAirportName };

/** Nó da timeline: o terminal (enriquecimento Cirium) entra ao lado do nome do
 *  aeroporto — "Florianópolis · Terminal 1"; sem nome, o terminal fica sozinho. O
 *  nome é o do cadastro do host (#4157), não a cidade crua do fornecedor. */
function toTimelineNode(
  endpoint: FlightDetailEndpoint,
  terminal: string | undefined,
  text: TimelineText,
): FlightTimelineLeg['origin'] {
  const name = withTerminal(text.airportName(endpoint.iata, endpoint.name), terminal, text.copy);
  return {
    time: fmtClock(endpoint.at),
    iata: endpoint.iata,
    ...(name ? { name } : {}),
  };
}

/** Caixa de parada ENTRE pernas: duração + aeroporto da conexão e, quando a perna
 *  seguinte carrega seu nº de voo, "continua no voo AD 2778" (senão degrada para o
 *  aviso de troca de aeronave, o comportamento anterior). */
function toStopLabel(
  leg: FlightDetailLegSource,
  next: FlightDetailLegSource,
  layover: string,
  text: TimelineText,
): string {
  const { copy } = text;
  const name = text.airportName(leg.destination.iata, leg.destination.name);
  const airport = name ? `${name} (${leg.destination.iata})` : leg.destination.iata;
  const nextFlight = [next.carrierCode, next.flightNumber].filter(Boolean).join(' ');
  const suffix = nextFlight
    ? ` · ${copy.continuesOn(nextFlight)}`
    : next.isConnection
      ? ` · ${copy.aircraftChange}`
      : '';
  return copy.connection(layover, airport) + suffix;
}

/** Linha de duração: "1h 15min de voo · Embraer 195 E2 · operado por GOL" —
 *  aeronave e operadora só quando presentes (a view já filtra "operado pela
 *  própria cia" no servidor). */
function toDurationLabel(leg: FlightDetailLegSource, copy: FlightDetailCopy): string | undefined {
  const dur = fmtDuration(leg.durationSeconds);
  if (!dur) return undefined;
  const operator = leg.operatedBy?.name ?? leg.operatedBy?.code;
  return [
    copy.flightDuration(dur),
    leg.aircraftName,
    operator ? copy.operatedBy(operator) : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Adapta as pernas do GET /flight/legs para a vista compartilhada — strings já
 *  formatadas e localizadas. A linha de duração ganha aeronave e operadora; a
 *  caixa de parada nomeia a conexão e o voo seguinte. */
function toTimelineLegs(
  legs: readonly FlightDetailLegSource[],
  text: TimelineText,
): FlightTimelineLeg[] {
  const { copy } = text;
  return legs.map((leg, i) => {
    const next = legs[i + 1];
    const durationLabel = toDurationLabel(leg, copy);
    // Caixa de parada só faz sentido ENTRE pernas (conexão): exige uma perna
    // seguinte e um layover real (>0). Última perna / voo direto não têm `next`
    // ⇒ sem caixa — evita a caixa "layover" solta que aparecia em voo direto.
    const layover = fmtDuration(next ? leg.layoverSeconds : undefined);
    const stop = next && layover ? toStopLabel(leg, next, layover, text) : undefined;
    const stats = onTimeStatLabel(leg.onTimePercent, leg.onTimeObservations, copy);
    const soldAs = soldAsLabel(leg.soldAs, copy);
    return {
      origin: toTimelineNode(leg.origin, leg.departureTerminal, text),
      destination: toTimelineNode(leg.destination, leg.arrivalTerminal, text),
      ...(durationLabel ? { durationLabel } : {}),
      ...(stats ? { statsLabel: stats } : {}),
      ...(soldAs ? { soldAsLabel: soldAs } : {}),
      ...(stop ? { stopLabel: stop } : {}),
    };
  });
}

/** A nota de procedência só aparece quando algum dado de parceiro está na tela. */
function hasPartnerData(legs: readonly FlightDetailLegSource[]): boolean {
  return legs.some((leg) => hasEnrichmentData(leg));
}

/** O aviso sob o detalhe enquanto as pernas não pousam: carregando ou falha.
 *  Quem veio pelos assentos de uma cotação vencida (a sessão de busca dura
 *  15 min) precisa saber que o mapa acabou, não que o detalhe falhou. */
function LegsStatus({
  state,
  focus,
  copy,
}: Readonly<{
  state: FlightDetailLegsState;
  focus: 'seats' | undefined;
  copy: FlightDetailCopy;
}>) {
  if (state.phase === 'loading') {
    return (
      <output className="fdv-status" style={{ display: 'block' }}>
        {copy.loading}
      </output>
    );
  }
  if (state.phase !== 'error') return null;
  return (
    <div className="fdv-status fdv-status--error" role="alert">
      {focus === 'seats' && state.stale ? copy.quoteExpired : copy.loadError}
    </div>
  );
}

/**
 * Drawer "Detalhe do voo" — slide-over à direita aberto pela lista de ofertas.
 * Monta o shell compartilhado `FlightDetailDrawerShell` (mesmo das ofertas e dos
 * cards in-chat) e desenha as pernas que o host buscou (`legs`) pela vista
 * `FlightDetailView`: cia + cada trecho com caixas de parada. O cabeçalho da cia
 * aparece já do `summary` enquanto as pernas carregam. O rodapé "Selecionar voo"
 * reusa o pick da oferta.
 *
 * Push × overlay pela régua `column` (1100px) do DS, a MESMA que o host lê para
 * empurrar `.chat-main`/`.fex-takeover` (chat_container.css, explorer.css): a
 * partir dela a lista segue visível e interativa ao lado ⇒ sem véu e sem
 * `aria-modal`; abaixo dela um painel de 600px não deixa largura de leitura
 * (#3239 C2: em 883 a lista ficava com ~283px, e em 768 sobrava uma faixa de
 * 168px clicável sob o painel), então o drawer COBRE como o `SegmentDetailDrawer`
 * dos cards — véu que fecha no clique, dialog modal com trap de Tab.
 *
 * Modo "mapa de assentos": o slot do host (`renderSeats`) abre o painel cheio
 * por `openPanel`, que troca o corpo do drawer SEM fechá-lo — o botão voltar do
 * shell (e o Esc, que o shell já wireia no `onClose`) volta pro detalhe em vez de
 * fechar. O rodapé "Selecionar voo" some nesse modo (superfície de leitura, sem
 * ação de pick dentro do mapa).
 */
export function FlightDetailPanel<L extends FlightDetailLegSource>({
  summary,
  legs: state,
  copy,
  locale,
  airportName,
  renderSeats,
  onClose,
  onSelect,
  focus,
}: Readonly<FlightDetailPanelProps<L>>) {
  const text: TimelineText = { copy, airportName };
  const pushed = useMediaQuery(atLeast('column'));
  const seatsRef = useRef<HTMLDivElement>(null);
  // Aberto por "Assentos": rola até a seção do mapa quando as pernas pousam
  // (`state.phase`), porque antes disso a seção não existe no DOM. `block:
  // 'start'` e sem `behavior: 'smooth'`: quem pediu assentos quer chegar lá, não
  // assistir à viagem.
  useEffect(() => {
    if (focus !== 'seats' || state.phase !== 'ready') return;
    seatsRef.current?.scrollIntoView({ block: 'start' });
  }, [focus, state.phase]);
  const [seatMap, setSeatMap] = useState<ReactNode>(null);
  // Round-trip: as pernas chegam com `direction` e o drawer vira DUAS seções
  // (Ida/Volta), cada uma com seu cabeçalho derivado das próprias pernas — o
  // `summary` do card (que descreve só a ida) deixa de mandar no conteúdo.
  const legs = state.phase === 'ready' ? state.legs : [];
  const outbound = legs.filter((leg) => leg.direction !== 'return');
  const inbound = legs.filter((leg) => leg.direction === 'return');
  const roundTrip = outbound.length > 0 && inbound.length > 0;
  // Rodapé só quando há ação de pick e o fetch não falhou (sem "selecionar" um
  // detalhe que não carregou).
  const footer =
    onSelect && state.phase !== 'error' ? (
      <button type="button" className="fdv-select" onClick={onSelect}>
        {copy.select}
        <ArrowRightIcon size={18} aria-hidden />
      </button>
    ) : undefined;

  return (
    <FlightDetailDrawerShell
      title={seatMap ? copy.seatMapTitle : copy.title}
      ariaLabel={copy.drawerAria}
      backLabel={copy.backAria}
      // Push: a conversa fica visível e interativa ao lado, então o `Sheet` NÃO é
      // modal (sem véu, sem trap). Overlay (abaixo de `column`): coberto e com o
      // véu do `Sheet`, é modal de verdade.
      modal={!pushed}
      // No modo mapa, o botão voltar/Esc do shell volta pro detalhe em vez de
      // fechar o drawer — só fecha de verdade quando já está no detalhe.
      onClose={
        seatMap
          ? () => {
              setSeatMap(null);
            }
          : onClose
      }
      {...(!seatMap && footer ? { footer } : {})}
    >
      {seatMap ? (
        seatMap
      ) : (
        <>
          {roundTrip ? (
            <>
              <section className="fdt-section">
                <FlightDetailView
                  header={toSectionHeader(outbound, summary, 'outbound', copy, locale)}
                  legs={toTimelineLegs(outbound, text)}
                />
              </section>
              <section className="fdt-section">
                <FlightDetailView
                  header={toSectionHeader(inbound, summary, 'return', copy, locale)}
                  legs={toTimelineLegs(inbound, text)}
                />
              </section>
            </>
          ) : (
            <FlightDetailView
              header={toDetailHeader(summary, copy)}
              legs={toTimelineLegs(legs, text)}
            />
          )}

          {state.phase === 'ready' && renderSeats && (
            // Embrulho com a ref, e não a ref na seção: a `FlightSeatMapSection`
            // devolve `null` quando nenhuma perna é elegível, e aí o
            // `scrollIntoView` cairia num alvo inexistente. Com o embrulho ele
            // rola até onde a seção ESTARIA — que é o comportamento certo: não
            // há mapa a mostrar, e a pessoa vê o fim do detalhe.
            <div ref={seatsRef}>{renderSeats(legs, setSeatMap)}</div>
          )}

          {hasPartnerData(legs) && <p className="fdt-partner-note">{copy.partnerDataNote}</p>}

          <LegsStatus state={state} focus={focus} copy={copy} />
        </>
      )}
    </FlightDetailDrawerShell>
  );
}
